import { ConfigProvider } from 'antd'
import ukUA from 'antd/locale/uk_UA'
import React from 'react'

import { BaseLoader } from './Commands/Loaders/BaseLoader'
import { ConfigKernelLoader } from './Commands/Loaders/ConfigKernelLoader'
import { I18nKernelLoader } from './Commands/Loaders/I18nKernelLoader'
import { ResourceKernelLoader } from './Commands/Loaders/ResourceKernelLoader'
import { RouteKernelLoader, type RouteLoaderResult } from './Commands/Loaders/RouteKernelLoader'
import { ServiceKernelLoader } from './Commands/Loaders/ServiceKernelLoader'

import { type IAppConfig } from './Contracts/Configs/IAppConfig'
import {
    type ServiceMap,
    type StoreMap,
    LoaderError,
    ServiceNotFoundError,
    StoreNotFoundError,
} from './Contracts/Kernel/IKernelTypes'
import type { ServiceContainer } from './Utils/ServiceContainer'

import { registerFieldStrategies } from './UI/Form/registerFieldStrategies'

import { loggerManager, profilerManager } from './Managers'

/**
 * Символ для зберігання Kernel в window об'єкті
 * Використовується для збереження стану під час HMR
 */
const KERNEL_INSTANCE_SYMBOL = '__FRONTEND_KERNEL_INSTANCE__' as const

/**
 * Ядро додатку - точка входу для ініціалізації всіх систем
 * Працює в стилі Symfony Kernel з явною ініціалізацією
 */
export class Kernel {
    private static instance: Kernel
    private isInitialized = false
    private services: ServiceMap = new Map()
    private stores: StoreMap = new Map()
    private loaders: Map<string, BaseLoader> = new Map()
    private serviceContainer?: ServiceContainer

    // Кешовані результати завантажувачів
    private appConfig?: IAppConfig
    private routeData?: RouteLoaderResult

    constructor() {
        // В development режимі використовуємо window для збереження стану під час HMR
        if (typeof window !== 'undefined' && import.meta.env?.DEV) {
            const existingKernel = (window as any)[KERNEL_INSTANCE_SYMBOL] as Kernel | undefined
            if (existingKernel) {
                // Cleanup старого екземпляру перед створенням нового
                this.cleanupOldInstance(existingKernel)
                Kernel.instance = existingKernel
                return existingKernel
            }
        }

        if (Kernel.instance) {
            return Kernel.instance
        }
        Kernel.instance = this

        // Зберігаємо в window для збереження під час HMR
        if (typeof window !== 'undefined' && import.meta.env?.DEV) {
            (window as any)[KERNEL_INSTANCE_SYMBOL] = this
            // Реєструємо HMR dispose handler
            this.setupHMRCleanup()
        }

        // Ініціалізуємо завантажувачі
        this.initializeLoaders()
    }

    /**
     * Налаштування cleanup при HMR
     */
    private setupHMRCleanup(): void {
        if (import.meta.hot) {
            import.meta.hot.dispose(() => {
                loggerManager.info('HMR: Очищення Kernel', 'kernel')
                this.cleanup()
            })
        }
    }

    /**
     * Очищення старого екземпляру при HMR
     */
    private cleanupOldInstance(oldInstance: Kernel): void {
        try {
            // Тут можна додати додаткову логіку очищення якщо потрібно
            loggerManager.debug('Повторне використання існуючого Kernel екземпляру', 'kernel')
        } catch (error) {
            loggerManager.warn('Помилка при очищенні старого екземпляру Kernel', 'kernel', undefined, { error })
        }
    }

    /**
     * Cleanup всіх ресурсів Kernel
     */
    public cleanup(): void {
        loggerManager.info('Початок cleanup Kernel', 'kernel')
        
        try {
            // Знищуємо всі сервіси через ServiceContainer lifecycle
            if (this.serviceContainer) {
                this.serviceContainer.destroyAll()
                    .then(() => {
                        loggerManager.debug('Всі сервіси знищені', 'kernel')
                        this.serviceContainer?.clear()
                    })
                    .catch((error) => {
                        loggerManager.error('Помилка при знищенні сервісів', 'kernel', undefined, { error })
                    })
            }
            
            // Очищуємо кеші
            this.services.clear()
            this.stores.clear()
            this.loaders.clear()
            
            // Скидаємо флаг ініціалізації
            this.isInitialized = false
            
            loggerManager.info('Kernel cleanup завершено', 'kernel')
        } catch (error) {
            loggerManager.error('Помилка при cleanup Kernel', 'kernel', undefined, { error })
        }
    }

    static getInstance(): Kernel {
        // В development режимі перевіряємо window спочатку
        if (typeof window !== 'undefined' && import.meta.env?.DEV) {
            const existingKernel = (window as any)[KERNEL_INSTANCE_SYMBOL] as Kernel | undefined
            if (existingKernel) {
                Kernel.instance = existingKernel
                return existingKernel
            }
        }

        if (!Kernel.instance) {
            Kernel.instance = new Kernel()
        }
        return Kernel.instance
    }

    /**
     * Ініціалізація завантажувачів
     */
    private initializeLoaders(): void {
        const resourceLoader = new ResourceKernelLoader()
        const routeLoader = new RouteKernelLoader()

        // Встановлюємо зв'язок між завантажувачами
        routeLoader.setResourceLoader(resourceLoader)

        this.loaders.set('config', new ConfigKernelLoader())
        this.loaders.set('i18n', new I18nKernelLoader())
        this.loaders.set('resource', resourceLoader)
        this.loaders.set('route', routeLoader)
        this.loaders.set('service', new ServiceKernelLoader())

        // Реєструємо профайлери для відлагодження
        this.initializeProfilers()
    }

    private initializeProfilers(): void {
        loggerManager.info('Ініціалізація профайлерів системи', 'kernel')
        profilerManager.setEnabled(true)
    }

    /**
     * Ініціалізація стратегій полів форм
     */
    private initializeFieldStrategies(): void {
        loggerManager.info('Ініціалізація стратегій полів форм', 'kernel')
        registerFieldStrategies()
        loggerManager.info('Стратегії полів форм успішно зареєстровано', 'kernel')
    }

    /**
     * Запуск всіх завантажувачів у порядку пріоритету
     */
    private async runLoaders(): Promise<void> {
        // Отримуємо всі завантажувачі і сортуємо за пріоритетом
        const loaderEntries = Array.from(this.loaders.entries())
        loaderEntries.sort(([, a], [, b]) => b.getPriority() - a.getPriority())

        // Запускаємо завантажувачі послідовно
        for (const [name, loader] of loaderEntries) {
            try {
                const result = await loader.load()

                // Зберігаємо результати залежно від типу завантажувача
                switch (name) {
                    case 'config':
                        this.appConfig = result as IAppConfig
                        this.services.set('config', result)
                        break
                    case 'route':
                        this.routeData = result as RouteLoaderResult
                        this.services.set('routes', result)
                        break
                    case 'service':
                        // Розпаковуємо всі сервіси з ServiceKernelLoader
                        if (result && typeof result === 'object') {
                            Object.entries(result).forEach(([serviceName, serviceInstance]) => {
                                this.services.set(serviceName, serviceInstance)
                            })
                        }
                        // Зберігаємо посилання на ServiceContainer
                        if (loader instanceof ServiceKernelLoader) {
                            this.serviceContainer = loader.getContainer()
                        }
                        break
                    case 'i18n':
                    case 'resource':
                        this.services.set(name, result)
                        break
                }
            } catch (error) {
                const loaderError = new LoaderError(
                    `Failed to load ${name}`,
                    name,
                    { originalError: error }
                )
                loggerManager.error(loaderError.message, 'kernel', { error: loaderError })
                throw loaderError
            }
        }
    }

    /**
     * Ініціалізація ядра системи
     */
    async initialize(): Promise<void> {
        if (this.isInitialized) {
            loggerManager.warn('Kernel вже ініціалізовано', 'kernel')
            return
        }

        try {
            // Завантажуємо дані через завантажувачі у порядку пріоритету
            await this.runLoaders()

            // Реєструємо стратегії полів форм
            this.initializeFieldStrategies()

            // Ініціалізуємо всі сервіси через ServiceContainer lifecycle
            if (this.serviceContainer) {
                await this.serviceContainer.initializeAll()
                loggerManager.info('Всі сервіси ініціалізовані', 'kernel')
            }

            this.isInitialized = true
            loggerManager.info('Kernel успішно ініціалізовано', 'kernel')
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            loggerManager.error(`Помилка ініціалізації Kernel: ${errorMessage}`, 'kernel')
            throw error
        }
    }



    /**
     * Отримання сервісу з контейнера
     * @throws {ServiceNotFoundError} якщо сервіс не знайдено і strict = true
     */
    getService<T = unknown>(name: string, strict = false): T | undefined {
        const service = this.services.get(name) as T | undefined
        
        if (!service && strict) {
            throw new ServiceNotFoundError(name)
        }
        
        return service
    }

    /**
     * Отримання store з контейнера
     * @throws {StoreNotFoundError} якщо store не знайдено і strict = true
     */
    getStore<T = unknown>(name: string, strict = false): T | undefined {
        const store = this.stores.get(name) as T | undefined
        
        if (!store && strict) {
            throw new StoreNotFoundError(name)
        }
        
        return store
    }

    /**
     * Реєстрація сервісу
     */
    registerService(name: string, service: unknown): void {
        if (this.services.has(name)) {
            loggerManager.warn(`Сервіс "${name}" вже зареєстровано, перезаписуємо`, 'kernel')
        }
        this.services.set(name, service)
        loggerManager.debug(`Сервіс "${name}" зареєстровано`, 'kernel')
    }

    /**
     * Реєстрація store
     */
    registerStore(name: string, store: unknown): void {
        if (this.stores.has(name)) {
            loggerManager.warn(`Store "${name}" вже зареєстровано, перезаписуємо`, 'kernel')
        }
        this.stores.set(name, store)
        loggerManager.debug(`Store "${name}" зареєстровано`, 'kernel')
    }

    /**
     * Отримання конфігурації додатку
     */
    getConfig(): IAppConfig | undefined {
        return this.appConfig
    }

    /**
     * Отримання маршрутів додатку
     */
    getRoutes(): RouteLoaderResult | undefined {
        return this.routeData
    }

    /**
     * Отримання завантажувача
     */
    getLoader(name: string): BaseLoader | undefined {
        return this.loaders.get(name)
    }

    /**
     * Перевірка статусу ініціалізації
     */
    isKernelInitialized(): boolean {
        return this.isInitialized
    }

    /**
     * Методи для відлагодження (тільки в development режимі)
     */
    getDebugInfo(): any {
        if (!import.meta.env?.DEV) {
            return null
        }

        return {
            loaders: this.loaders,
            services: this.services,
            stores: this.stores,
            appConfig: this.appConfig,
            routeData: this.routeData,
            isInitialized: this.isInitialized,
        }
    }

    getLoadersDebugInfo(): Map<string, BaseLoader> {
        if (!import.meta.env?.DEV) {
            return new Map()
        }
        return this.loaders
    }

    getServicesDebugInfo(): ServiceMap {
        if (!import.meta.env?.DEV) {
            return new Map()
        }
        return this.services
    }

    /**
     * Отримати всі зареєстровані сервіси
     */
    getAllServices(): ServiceMap {
        return new Map(this.services)
    }

    /**
     * Отримати всі зареєстровані stores
     */
    getAllStores(): StoreMap {
        return new Map(this.stores)
    }

    /**
     * Створення React контексту провайдера на основі конфігурації
     */
    createApplicationProvider(): React.ComponentType<{ children: React.ReactNode }> {
        const config = this.appConfig

        return ({ children }: { children: React.ReactNode }) => {
            // Використовуємо Ant Design ConfigProvider із завантаженою конфігурацією
            const antdConfig = {
                locale: ukUA,
                theme: {
                    token: {
                        colorPrimary: config?.theme?.primaryColor || '#1890ff',
                        borderRadius: config?.theme?.borderRadius || 6,
                    },
                },
            }

            return React.createElement(ConfigProvider, antdConfig, children)
        }
    }
}

// Експорт singleton екземпляра
// Експорт ледачого створення екземпляра
export function getKernelInstance(): Kernel {
    return Kernel.getInstance()
}

// Для зворотної сумісності - створення екземпляра за запитом
export const kernel = new Proxy({} as Kernel, {
    get(_target, prop) {
        const instance = Kernel.getInstance()
        return instance[prop as keyof Kernel]
    },
})

// Kernel ініціалізується через loader/clientLoader в root.tsx:
// - loader() виконується на сервері (SSR режим)
// - clientLoader() виконується на клієнті (SPA + hydration)
// Це забезпечує правильну ініціалізацію в обох режимах
